import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, TrendingUp, Users, Gauge, Target } from 'lucide-react';
import { Badge, Select } from '@/components/ui';
import { GlassLoadingSpinner, CollapsibleMetricsSection } from '@/design-system';
import { apiCall } from '@/utils/api';

// Design System Imports
import { MetricCard } from '@/design-system/components/data-display/MetricCard';
import { CircularProgress } from '@/design-system/components/feedback/CircularProgress';
import { ProgressBar } from '@/design-system/components/feedback/ProgressBar';
import { GlassPageHeader } from '@/design-system/components/layout/GlassPageHeader';

interface EpicBreakdown {
  epicId: string;
  epicName: string;
  priority: string;
  status: string;
  isCategory: string;
  isName: string;
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
}

interface TeamCapacity {
  teamId: string;
  teamName: string;
  memberCount: number;
  costProfile: {
    id: string;
    name: string;
    hourlyRate: number;
  };
  capacity: {
    total: number;
    allocated: number;
    available: number;
    utilization: number;
    overCapacity: number;
    isOverCapacity: boolean;
    isNearCapacity: boolean;
    isHealthy: boolean;
  };
  cost: {
    total: number;
    allocated: number;
    available: number;
  };
  epicBreakdown: EpicBreakdown[];
}

interface ProgramCapacity {
  programId: string;
  programName: string;
  programCategory: string;
  leadId?: string;
  leadName?: string;
  capacity: {
    total: number;
    allocated: number;
    available: number;
    utilization: number;
    overCapacity: number;
    isOverCapacity: boolean;
    isNearCapacity: boolean;
    isHealthy: boolean;
  };
  cost: {
    total: number;
    allocated: number;
    available: number;
    costPerWeek: number;
  };
  teams: TeamCapacity[];
  epicBreakdown: EpicBreakdown[];
  epicsByCategory: {
    BUSINESS: EpicBreakdown[];
    TECH: EpicBreakdown[];
    SUPPORT: EpicBreakdown[];
  };
  insights: {
    totalEpics: number;
    highPriorityEpics: number;
    additionalCapacityNeeded: number;
    atRiskPriorities: Array<{
      epicName: string;
      isName: string;
      gap: number;
    }>;
  };
}

interface PlanningPeriod {
  id: string;
  name: string;
  weeks: number;
}

interface Summary {
  totalPrograms: number;
  overCapacityPrograms: number;
  nearCapacityPrograms: number;
  healthyPrograms: number;
  totalCapacity: number;
  totalAllocated: number;
  totalAvailable: number;
  overallUtilization: number;
  totalAdditionalCapacityNeeded: number;
}

interface CapacityData {
  planningPeriod: PlanningPeriod;
  programs: ProgramCapacity[];
  summary: Summary;
}

const ProgramCapacityPage: React.FC = () => {
  const [data, setData] = useState<CapacityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [selectedPlanningPeriodId, setSelectedPlanningPeriodId] = useState<string>('');
  const [showOnlyOverCapacity, setShowOnlyOverCapacity] = useState(false);
  const [planningPeriods, setPlanningPeriods] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    fetchPlanningPeriods();
  }, []);

  useEffect(() => {
    // Always fetch capacity data, even without a planning period selected
    fetchCapacityData();
  }, [selectedPlanningPeriodId]);

  const fetchPlanningPeriods = async () => {
    try {
      console.log('[ProgramCapacity] Fetching planning periods...');
      const response = await apiCall('GET', '/planning-periods');
      console.log('[ProgramCapacity] Planning periods response:', response);

      const responseData = response.data as { data?: unknown[] } | unknown[];
      const periods = (responseData && typeof responseData === 'object' && 'data' in responseData ? responseData.data : responseData) || [];
      console.log('[ProgramCapacity] Extracted periods:', periods);

      // Ensure periods is an array
      const periodsArray = Array.isArray(periods) ? periods : [];
      setPlanningPeriods(periodsArray);

      // Set the first planning period as default
      if (periodsArray.length > 0 && !selectedPlanningPeriodId) {
        setSelectedPlanningPeriodId(periodsArray[0].id);
      }
    } catch (err) {
      console.error('[ProgramCapacity] Error fetching planning periods:', err);
      setPlanningPeriods([]); // Ensure it's always an array
    }
  };

  const fetchCapacityData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedPlanningPeriodId) {
        params.append('planningPeriodId', selectedPlanningPeriodId);
      }

      console.log('[ProgramCapacity] Fetching capacity data with params:', params.toString());
      const response = await apiCall('GET', `/capacity/programs?${params}`);
      console.log('[ProgramCapacity] Received response:', response);

      const capacityResponseData = response.data as { data?: unknown } | unknown;
      const capacityData = (capacityResponseData && typeof capacityResponseData === 'object' && 'data' in capacityResponseData ? capacityResponseData.data : capacityResponseData);
      console.log('[ProgramCapacity] Setting capacity data:', capacityData);
      setData(capacityData);
    } catch (err: any) {
      console.error('[ProgramCapacity] Error fetching capacity data:', err);
      console.error('[ProgramCapacity] Error details:', err.response?.data || err.message);
      setError(err.response?.data?.message || err.message || 'Failed to load capacity data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleProgram = (programId: string) => {
    const newExpanded = new Set(expandedPrograms);
    if (newExpanded.has(programId)) {
      newExpanded.delete(programId);
    } else {
      newExpanded.add(programId);
    }
    setExpandedPrograms(newExpanded);
  };

  const toggleTeam = (teamId: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };


  const formatCost = (cost: number) => {
    return `$${(cost / 1000000).toFixed(2)}M`;
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return <Badge variant="danger">High</Badge>;
      case 'MEDIUM':
        return <Badge variant="warning">Medium</Badge>;
      case 'LOW':
        return <Badge variant="secondary">Low</Badge>;
      default:
        return <Badge variant="default">{priority}</Badge>;
    }
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'BUSINESS':
        return <Badge variant="primary">Business</Badge>;
      case 'TECH':
        return <Badge variant="info">Tech</Badge>;
      case 'SUPPORT':
        return <Badge variant="secondary">Support</Badge>;
      default:
        return <Badge variant="default">{category}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading capacity data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg">
        <p className="text-status-error">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-gray-600">No capacity data available</p>
      </div>
    );
  }

  const filteredPrograms = showOnlyOverCapacity
    ? data.programs.filter(p => p.capacity.isOverCapacity || p.capacity.isNearCapacity)
    : data.programs;

  return (
    <div className="glass-page-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <GlassPageHeader
          title="Program & Team Capacity"
          subtitle={`Planning Period: ${data?.planningPeriod?.name || 'All Periods'}`}
          description="Hierarchical view of program capacity with EPIC allocation breakdown by team"
          icon={<Gauge className="w-6 h-6" />}
          iconGradient="blue-violet"
          metadata={[
            {
              icon: <Users className="w-4 h-4" />,
              label: 'Programs',
              value: String(data?.summary?.totalPrograms || 0)
            },
            {
              icon: <Target className="w-4 h-4" />,
              label: 'Utilization',
              value: `${data?.summary?.overallUtilization?.toFixed(1) || 0}%`
            },
          ]}
          className="mb-6 section-reveal"
        />

        {/* Summary Metrics - Glass MetricCards */}
        <CollapsibleMetricsSection storageKey="program-capacity">
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 section-reveal"
            style={{ animationDelay: '100ms' }}
          >
            <MetricCard
              title="Total Capacity"
              value={`${data.summary.totalCapacity.toFixed(0)} TW`}
              subtitle={formatCost(data.summary.totalCapacity * 40 * 89)}
              icon={<Users className="w-5 h-5" />}
              iconColor="blue"
              progress={100}
            />

            <MetricCard
              title="Utilization"
              value={`${data.summary.overallUtilization.toFixed(1)}%`}
              subtitle={`${data.summary.totalAllocated.toFixed(0)} TW allocated`}
              icon={<TrendingUp className="w-5 h-5" />}
              iconColor={
                data.summary.overallUtilization > 100 ? 'rose' :
                data.summary.overallUtilization >= 85 ? 'amber' : 'emerald'
              }
              progress={Math.min(data.summary.overallUtilization, 100)}
              trend={{
                value: Math.abs(100 - data.summary.overallUtilization),
                direction: data.summary.overallUtilization > 100 ? 'up' : 'down'
              }}
            />

            <MetricCard
              title="Capacity Status"
              value={data.summary.overCapacityPrograms}
              subtitle={`${data.summary.nearCapacityPrograms} near capacity`}
              icon={<AlertTriangle className="w-5 h-5" />}
              iconColor="rose"
            />

            <MetricCard
              title="Additional Needed"
              value={`${data.summary.totalAdditionalCapacityNeeded.toFixed(0)} TW`}
              subtitle="for high-priority EPICs"
              icon={<Target className="w-5 h-5" />}
              iconColor="amber"
            />
          </div>
        </CollapsibleMetricsSection>

        {/* Filters - Glass Toolbar */}
        <div
          className="glass rounded-xl p-4 section-reveal"
          style={{ animationDelay: '200ms' }}
        >
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                Planning Period
              </label>
              <Select
                value={selectedPlanningPeriodId}
                onChange={(e) => setSelectedPlanningPeriodId(e.target.value)}
                options={Array.isArray(planningPeriods) ? planningPeriods.map(p => ({ value: p.id, label: p.name })) : []}
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-3 mt-6">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showOnlyOverCapacity}
                  onChange={(e) => setShowOnlyOverCapacity(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-[var(--accent)] focus:ring-offset-0"
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Show only over/near capacity
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Programs List - Glass Collapsible Cards */}
        <div
          className="space-y-4 section-reveal"
          style={{ animationDelay: '300ms' }}
        >
          {filteredPrograms.map((program, index) => (
            <div
              key={program.programId}
              className="glass-strong rounded-xl overflow-hidden hover-lift"
              style={{ animationDelay: `${350 + index * 50}ms` }}
            >
              {/* Program Header - Clickable */}
              <div
                className={`p-5 cursor-pointer transition-all duration-200 ${
                  program.capacity.isOverCapacity
                    ? 'bg-gradient-to-r from-rose-50/80 to-transparent dark:from-rose-900/30 dark:to-transparent border-l-4 border-rose-500'
                    : program.capacity.isNearCapacity
                    ? 'bg-gradient-to-r from-amber-50/80 to-transparent dark:from-amber-900/30 dark:to-transparent border-l-4 border-amber-500'
                    : 'bg-gradient-to-r from-emerald-50/80 to-transparent dark:from-emerald-900/30 dark:to-transparent border-l-4 border-emerald-500'
                }`}
                onClick={() => toggleProgram(program.programId)}
              >
                <div className="flex items-center justify-between">
                  {/* Left: Expand Icon + Status + Program Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <button className="p-1 rounded-lg hover:bg-white/50 transition-colors">
                      {expandedPrograms.has(program.programId) ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>

                    {/* Utilization Ring */}
                    <CircularProgress
                      value={Math.min(program.capacity.utilization, 100)}
                      size="md"
                      color={
                        program.capacity.isOverCapacity ? 'rose' :
                        program.capacity.isNearCapacity ? 'amber' : 'emerald'
                      }
                      showValue
                    />

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{program.programName}</h3>
                      {program.leadName && (
                        <p className="text-sm text-gray-500">Lead: {program.leadName}</p>
                      )}
                    </div>
                  </div>

                  {/* Right: Metrics + Badge */}
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Capacity</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {program.capacity.allocated.toFixed(0)} / {program.capacity.total.toFixed(0)} TW
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teams</p>
                      <p className="text-sm font-semibold text-gray-900">{program.teams.length}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">EPICs</p>
                      <p className="text-sm font-semibold text-gray-900">{program.insights.totalEpics}</p>
                    </div>

                    {/* Mini Progress Bar */}
                    <div className="w-32">
                      <ProgressBar
                        value={Math.min(program.capacity.utilization, 100)}
                        color={
                          program.capacity.isOverCapacity ? 'rose' :
                          program.capacity.isNearCapacity ? 'amber' : 'emerald'
                        }
                        size="sm"
                        showShimmer
                        gradient
                      />
                    </div>

                    {/* Status Badge */}
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                      program.capacity.isOverCapacity
                        ? 'bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-700'
                        : program.capacity.isNearCapacity
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-700'
                        : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-700'
                    }`}>
                      {program.capacity.isOverCapacity ? 'Over' :
                       program.capacity.isNearCapacity ? 'Near' : 'Healthy'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedPrograms.has(program.programId) && (
                <div className="border-t border-gray-100/50 bg-white/30 p-5">
                  {/* At-Risk Alert Section */}
                  {program.insights.atRiskPriorities.length > 0 && (
                    <div className="mb-5 p-4 rounded-xl bg-gradient-to-r from-rose-50 to-amber-50/50 dark:from-rose-900/30 dark:to-amber-900/20 border border-rose-100 dark:border-rose-800">
                      <h4 className="text-sm font-semibold text-rose-900 dark:text-rose-300 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        High Priority EPICs At Risk ({program.insights.atRiskPriorities.length})
                      </h4>
                      <div className="space-y-2">
                        {program.insights.atRiskPriorities.map((risk, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-sm py-2 px-3 bg-white/60 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                              <span className="font-medium text-gray-900">{risk.epicName}</span>
                              <span className="text-gray-500">({risk.isName})</span>
                            </div>
                            <span className="font-semibold text-rose-600">
                              +{risk.gap.toFixed(1)} TW needed
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Teams Section */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      Teams ({program.teams.length})
                    </h4>
                    <div className="space-y-2">
                      {program.teams.map(team => (
                        <div
                          key={team.teamId}
                          className={`glass rounded-xl overflow-hidden transition-all duration-200 ${
                            team.capacity.isOverCapacity
                              ? 'border-l-4 border-rose-400'
                              : team.capacity.isNearCapacity
                              ? 'border-l-4 border-amber-400'
                              : 'border-l-4 border-emerald-400'
                          }`}
                        >
                          {/* Team Header */}
                          <div
                            className="p-4 cursor-pointer hover:bg-white/50 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTeam(team.teamId);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                {expandedTeams.has(team.teamId) ? (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-gray-400" />
                                )}

                                {/* Team Utilization Ring - Small */}
                                <CircularProgress
                                  value={Math.min(team.capacity.utilization, 100)}
                                  size="sm"
                                  color={
                                    team.capacity.isOverCapacity ? 'rose' :
                                    team.capacity.isNearCapacity ? 'amber' : 'emerald'
                                  }
                                  showValue
                                />

                                <div>
                                  <p className="text-sm font-medium text-gray-900">{team.teamName}</p>
                                  <p className="text-xs text-gray-500">{team.memberCount} members</p>
                                </div>
                              </div>

                              <div className="flex items-center gap-4">
                                <div className="text-right">
                                  <p className="text-xs text-gray-500">Capacity</p>
                                  <p className="text-xs font-medium text-gray-900">
                                    {team.capacity.allocated.toFixed(0)} / {team.capacity.total.toFixed(0)} TW
                                  </p>
                                </div>

                                <div className="w-20">
                                  <ProgressBar
                                    value={Math.min(team.capacity.utilization, 100)}
                                    color={
                                      team.capacity.isOverCapacity ? 'rose' :
                                      team.capacity.isNearCapacity ? 'amber' : 'emerald'
                                    }
                                    size="sm"
                                    gradient
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Team EPIC Breakdown */}
                          {expandedTeams.has(team.teamId) && team.epicBreakdown.length > 0 && (
                            <div className="px-4 pb-4 border-t border-gray-100/50">
                              <h5 className="text-xs font-semibold text-gray-700 my-3 uppercase tracking-wide">
                                EPIC Allocations ({team.epicBreakdown.length})
                              </h5>
                              <div className="space-y-1">
                                {team.epicBreakdown.map(epic => (
                                  <div
                                    key={epic.epicId}
                                    className="flex items-center justify-between text-xs py-2 px-3 bg-white/60 rounded-lg hover:bg-white/80 transition-colors"
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      <span className="font-medium text-gray-900">{epic.epicName}</span>
                                      {getPriorityBadge(epic.priority)}
                                      {getCategoryBadge(epic.isCategory)}
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-gray-500">{epic.isName}</span>
                                      <span className="font-semibold text-gray-900">
                                        {epic.allocatedTeamWeeks.toFixed(1)} TW
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
        ))}

        {filteredPrograms.length === 0 && (
          <div className="glass rounded-xl p-12 text-center">
            <p className="text-gray-500">No programs match the selected filters</p>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProgramCapacityPage;
